import React from 'react';

interface ProgressData {
  phase: 'starting' | 'processing' | 'sorting' | 'completed' | 'failed';
  current: number;
  total: number;
  percentage: number;
  message: string;
  batch?: number;
  totalBatches?: number;
  estimatedTimeLeft?: number;
  error?: string;
}

interface ProgressBarProps {
  progress: ProgressData;
  className?: string;
}

export const ProgressBar: React.FC<ProgressBarProps> = ({ progress, className = '' }) => {
  const getPhaseColor = (phase: string) => {
    switch (phase) {
      case 'starting':
        return 'bg-blue-500';
      case 'processing':
        return 'bg-yellow-500';
      case 'sorting':
        return 'bg-indigo-500';
      case 'completed':
        return 'bg-green-500';
      case 'failed':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getPhaseIcon = (phase: string) => {
    switch (phase) {
      case 'starting':
        return '🚀';
      case 'processing':
        return '⚙️';
      case 'sorting':
        return '📊';
      case 'completed':
        return '✅';
      case 'failed':
        return '❌';
      default:
        return '⏳';
    }
  };

  const formatTime = (seconds: number) => {
    if (seconds < 60) {
      return `${seconds}s`;
    } else if (seconds < 3600) {
      return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    } else {
      return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
    }
  };

  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-6 shadow-sm ${className}`}>
      {/* En-tête avec icône et phase */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <span className="text-2xl">{getPhaseIcon(progress.phase)}</span>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Génération du rapport MinIO
            </h3>
            <p className="text-sm text-gray-600 capitalize">
              {progress.phase === 'processing' ? 'Traitement en cours' :
               progress.phase === 'starting' ? 'Initialisation' :
               progress.phase === 'sorting' ? 'Finalisation' :
               progress.phase === 'completed' ? 'Terminé' :
               progress.phase === 'failed' ? 'Erreur' : progress.phase}
            </p>
          </div>
        </div>
        
        {/* Pourcentage */}
        <div className="text-right">
          <div className="text-2xl font-bold text-gray-900">
            {progress.percentage}%
          </div>
          {progress.batch && progress.totalBatches && (
            <div className="text-sm text-gray-500">
              Lot {progress.batch}/{progress.totalBatches}
            </div>
          )}
        </div>
      </div>

      {/* Barre de progression */}
      <div className="mb-4">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>{progress.message}</span>
          {progress.estimatedTimeLeft && (
            <span>~{formatTime(progress.estimatedTimeLeft)} restant</span>
          )}
        </div>
        
        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
          <div 
            className={`h-full transition-all duration-300 ease-out ${getPhaseColor(progress.phase)}`}
            style={{ width: `${Math.max(progress.percentage, 2)}%` }}
          >
            {/* Animation de pulsation pour les phases actives */}
            {(progress.phase === 'processing' || progress.phase === 'starting') && (
              <div className="h-full w-full bg-white bg-opacity-30 animate-pulse"></div>
            )}
          </div>
        </div>
      </div>

      {/* Détails */}
      <div className="flex justify-between text-sm text-gray-500">
        <span>
          {progress.current} / {progress.total} buckets traités
        </span>
        {progress.phase === 'failed' && progress.error && (
          <span className="text-red-600 font-medium">
            Erreur: {progress.error}
          </span>
        )}
        {progress.phase === 'completed' && (
          <span className="text-green-600 font-medium">
            Génération terminée avec succès !
          </span>
        )}
      </div>

      {/* Barre de statut pour les phases */}
      <div className="mt-4 flex space-x-2">
        {['starting', 'processing', 'sorting', 'completed'].map((phase, index) => (
          <div
            key={phase}
            className={`flex-1 h-1 rounded-full ${
              progress.phase === phase
                ? getPhaseColor(phase)
                : index < ['starting', 'processing', 'sorting', 'completed'].indexOf(progress.phase)
                ? 'bg-green-300'
                : 'bg-gray-200'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default ProgressBar;
